/**
 * FORGE-162: advisor tool — portal mirror of Forge's
 * src/orchestrator/shared/advisor.ts. Validates the models.advisor block with
 * the SAME rules the orchestrator enforces, so the editor's "valid" matches what
 * actually runs. Keep in sync with the orchestrator TS + agent/lib/advisor.js.
 *
 * Pure + dependency-free.
 */

export const DEFAULT_ADVISOR_MODEL = 'claude-opus-5-5'; // FORGE-387

/** Models that may act as a general advisor (legacy rank rule). */
const ADVISOR_FAMILIES = new Set(['opus-5-5', 'opus-5', 'opus-4-7', 'opus-4-8']);
const FAMILY_RANK: Record<string, number> = {
  'haiku-5-5': 1, 'haiku-4-5': 1, 'sonnet-4-6': 2, 'sonnet-5': 2, 'sonnet-5-5': 2, 'opus-4-6': 3, 'opus-4-7': 4,
  'opus-4-8': 5, 'opus-5': 6, 'opus-5-5': 7, 'fable-5': 3, 'mythos-5': 3,
};

// FORGE-387 (2026-10-04): explicit compatibility table, transcribed from
// platform.claude.com/docs/en/agents-and-tools/tool-use/advisor-tool. The old
// rank rule wrongly rejected Opus 5.5 as an advisor and mis-classified Sonnet
// 5.5 as Sonnet 5 (substring match). Executors not in the table (Sonnet 4.6,
// Opus 4.x) keep the legacy rank rule. `fable-5`/`mythos-5` also cover 5.1.
const ADVISOR_COMPAT: Record<string, string[]> = {
  'haiku-4-5':  ['sonnet-4-6', 'sonnet-5', 'sonnet-5-5', 'opus-4-6', 'opus-4-7', 'opus-4-8', 'opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],
  'haiku-5-5':  ['sonnet-5', 'sonnet-5-5', 'opus-4-7', 'opus-4-8', 'opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],   // FORGE-411
  'sonnet-5':   ['sonnet-4-6', 'sonnet-5', 'sonnet-5-5', 'opus-4-7', 'opus-4-8', 'opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],
  'sonnet-5-5': ['sonnet-5-5', 'opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],
  'opus-5':     ['opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],
  'opus-5-5':   ['opus-5', 'opus-5-5', 'fable-5', 'mythos-5'],
};

export function modelFamily(modelId: string | undefined | null): string | null {
  if (!modelId) return null;
  const m = modelId.toLowerCase();
  for (const fam of ['haiku-5-5', 'haiku-4-5', 'sonnet-4-6', 'sonnet-5-5', 'sonnet-5', 'opus-5-5', 'opus-5', 'opus-4-8', 'opus-4-7', 'opus-4-6', 'fable-5', 'mythos-5']) {
    if (m.includes(fam)) return fam;
  }
  return null;
}

export function isValidAdvisorPair(executorModel: string, advisorModel: string): boolean {
  const ef = modelFamily(executorModel);
  const af = modelFamily(advisorModel);
  if (!ef || !af) return false;
  const compat = ADVISOR_COMPAT[ef];
  if (compat) return compat.includes(af);
  if (!ADVISOR_FAMILIES.has(af)) return false;
  return (FAMILY_RANK[af] ?? 0) >= (FAMILY_RANK[ef] ?? 0);
}

export interface AdvisorConfig {
  enabled: boolean;
  model: string;
  max_uses?: number;
  agents: string[];
}

/** Advisor models the picker offers. */
// FORGE-387: Opus 5.5 is the recommended default; Opus 5 and 4-7/4-8 kept for
// projects still configured with those ids.
export const ADVISOR_MODEL_OPTIONS = ['claude-opus-5-5', 'claude-opus-5', 'claude-opus-4-8', 'claude-opus-4-7'] as const;

/** Validate the advisor config. Returns errors ([] when valid/disabled). */
export function validateAdvisorConfig(cfg: AdvisorConfig, plan: string): string[] {
  if (!cfg || !cfg.enabled) return [];
  const errs: string[] = [];
  if (plan !== 'enterprise') errs.push('Advisor requires the Enterprise plan');
  if (!cfg.model || !modelFamily(cfg.model)) {
    errs.push(`Advisor model "${cfg.model ?? ''}" is not a recognized model id`);
  } else if (!ADVISOR_FAMILIES.has(modelFamily(cfg.model)!)) {
    errs.push(`Advisor model "${cfg.model}" must be Opus 5.5 or Opus 5 (or legacy Opus 4.7/4.8)`);
  }
  if (cfg.max_uses != null && (!Number.isInteger(cfg.max_uses) || cfg.max_uses < 1 || cfg.max_uses > 20)) {
    errs.push('Advisor max uses must be an integer 1–20');
  }
  if (!Array.isArray(cfg.agents) || cfg.agents.length === 0) {
    errs.push('Pick at least one agent to attach the advisor to');
  }
  return errs;
}
